import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import type { OrderWithRelations as BaseOrderWithRelations } from "@ckgo2/shared/types";
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  Calendar,
  Users,
  Clock,
  FileText,
  CreditCard,
  ExternalLink,
} from "lucide-react";
import { formatPrice, formatDate } from "@ckgo2/shared/utils";
import CommunicationTimeline from "@/components/orders/CommunicationTimeline";
import StatusActions from "@/components/orders/StatusActions";

// Use the base type directly (it already includes communications)
type OrderWithRelations = BaseOrderWithRelations;

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  inquiry: { label: "Poptávka", className: "bg-gray-100 text-gray-700" },
  quoted: { label: "Naceneno", className: "bg-blue-100 text-blue-700" },
  confirmed: { label: "Potvrzeno", className: "bg-purple-100 text-purple-700" },
  deposit_paid: { label: "Záloha uhrazena", className: "bg-warning-100 text-warning-700" },
  fully_paid: { label: "Plně zaplaceno", className: "bg-success-100 text-success-700" },
  completed: { label: "Dokončeno", className: "bg-success-100 text-success-700" },
  cancelled: { label: "Zrušeno", className: "bg-error-100 text-error-700" },
};

const WORKFLOW_STEPS = [
  { status: "inquiry", label: "Poptávka" },
  { status: "quoted", label: "Naceneno" },
  { status: "confirmed", label: "Potvrzeno" },
  { status: "deposit_paid", label: "Záloha" },
  { status: "fully_paid", label: "Zaplaceno" },
  { status: "completed", label: "Dokončeno" },
];

export default function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["order", id],
    queryFn: () => api.get<ApiResponse<OrderWithRelations>>(`/api/admin/orders/${id}`),
  });

  const order = data?.data;

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="card text-center text-error-600">
        Chyba při načítání objednávky.
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.inquiry;
  const currentStepIndex = WORKFLOW_STEPS.findIndex((s) => s.status === order.status);
  const isCancelled = order.status === "cancelled";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/orders"
            className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Objednávka #{order.id.slice(-8)}
              </h1>
              <span className={`badge ${statusConfig.className}`}>
                {statusConfig.label}
              </span>
            </div>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              Vytvořeno: {formatDate(order.createdAt)}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <a
            href={`/api/admin/documents/contract/${order.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost"
          >
            <FileText className="h-5 w-5" />
            Smlouva
          </a>
          <a
            href={`/api/admin/documents/invoice/${order.id}?type=${
              order.status === "confirmed" ? "deposit" :
              order.status === "deposit_paid" ? "full" : "final"
            }`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost"
          >
            <CreditCard className="h-5 w-5" />
            Faktura
          </a>
        </div>
      </div>

      {/* Workflow */}
      {!isCancelled && (
        <div className="card">
          <div className="flex items-center justify-between">
            {WORKFLOW_STEPS.map((step, index) => {
              const isCompleted = index < currentStepIndex;
              const isCurrent = index === currentStepIndex;

              return (
                <div key={step.status} className="flex flex-1 items-center">
                  <div className="flex flex-col items-center">
                    <div
                      className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-medium ${
                        isCompleted
                          ? "bg-success-500 text-white"
                          : isCurrent
                            ? "bg-primary-500 text-white"
                            : "bg-gray-200 text-gray-500 dark:bg-gray-700"
                      }`}
                    >
                      {index + 1}
                    </div>
                    <span
                      className={`mt-2 text-xs font-medium ${
                        isCurrent
                          ? "text-primary-600 dark:text-primary-400"
                          : "text-gray-500"
                      }`}
                    >
                      {step.label}
                    </span>
                  </div>
                  {index < WORKFLOW_STEPS.length - 1 && (
                    <div
                      className={`mx-2 h-0.5 flex-1 ${
                        index < currentStepIndex
                          ? "bg-success-500"
                          : "bg-gray-200 dark:bg-gray-700"
                      }`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Cancelled warning */}
      {isCancelled && (
        <div className="rounded-lg bg-error-50 p-4 dark:bg-error-900/20">
          <p className="font-medium text-error-700 dark:text-error-400">
            Tato objednávka byla zrušena
          </p>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main content */}
        <div className="space-y-6 lg:col-span-2">
          {/* Trip info */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Zájezd
            </h2>

            <div className="flex gap-4">
              {order.trip?.featuredImage && (
                <img
                  src={order.trip.featuredImage}
                  alt={order.trip.title}
                  className="h-24 w-32 rounded-lg object-cover"
                />
              )}
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <h3 className="font-medium text-gray-900 dark:text-white">
                    {order.trip?.title}
                  </h3>
                  {order.trip?.slug && (
                    <a
                      href={`/zajezd/${order.trip.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-700"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  )}
                </div>
                <div className="mt-2 grid gap-2 text-sm text-gray-600 dark:text-gray-400 sm:grid-cols-2">
                  {order.tripDate && (
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      {formatDate(order.tripDate.startDate)} -{" "}
                      {formatDate(order.tripDate.endDate)}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    {order.adultsCount || 1} dospělých
                    {order.childrenCount ? `, ${order.childrenCount} dětí` : ""}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Price breakdown */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Cenová kalkulace
            </h2>

            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">
                  Cena × {order.adultsCount || 1} osob
                </span>
                <span className="text-gray-900 dark:text-white">
                  {formatPrice(order.totalPrice || 0)}
                </span>
              </div>
              {order.depositAmount && order.depositAmount > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Záloha</span>
                  <span className="text-gray-900 dark:text-white">
                    {formatPrice(order.depositAmount)}
                  </span>
                </div>
              )}
              <div className="border-t border-gray-200 pt-3 dark:border-gray-700">
                <div className="flex justify-between text-lg font-semibold">
                  <span className="text-gray-900 dark:text-white">Celkem</span>
                  <span className="text-primary-600">
                    {formatPrice(order.totalPrice || 0)}
                  </span>
                </div>
              </div>
            </div>

            {/* Payment status */}
            <div className="mt-6 border-t border-gray-200 pt-4 dark:border-gray-700">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Záloha</span>
                <span className="font-medium text-success-600">
                  {order.depositPaidAt ? formatPrice(order.depositAmount || 0) : formatPrice(0)}
                </span>
              </div>
              <div className="mt-2 flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">
                  Stav
                </span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {order.fullPaymentPaidAt ? "Plně zaplaceno" : order.depositPaidAt ? "Záloha uhrazena" : "Čeká na platbu"}
                </span>
              </div>
            </div>
          </div>

          {/* Customer notes */}
          {order.customerNotes && (
            <div className="card">
              <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                Poznámky zákazníka
              </h2>
              <p className="text-gray-600 dark:text-gray-400">{order.customerNotes}</p>
            </div>
          )}

          {/* Communication timeline */}
          <div className="card">
            <CommunicationTimeline
              orderId={order.id}
              customerId={order.customerId}
              communications={order.communications || []}
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Customer info */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Zákazník
            </h2>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400">
                  <User className="h-6 w-6" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {order.customer?.firstName} {order.customer?.lastName}
                  </p>
                  <Link
                    to={`/customers/${order.customerId}`}
                    className="text-sm text-primary-600 hover:text-primary-700"
                  >
                    Zobrazit profil
                  </Link>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <a
                  href={`mailto:${order.customer?.email}`}
                  className="flex items-center gap-2 text-gray-600 hover:text-primary-600 dark:text-gray-400"
                >
                  <Mail className="h-4 w-4" />
                  {order.customer?.email}
                </a>
                {order.customer?.phone && (
                  <a
                    href={`tel:${order.customer.phone}`}
                    className="flex items-center gap-2 text-gray-600 hover:text-primary-600 dark:text-gray-400"
                  >
                    <Phone className="h-4 w-4" />
                    {order.customer.phone}
                  </a>
                )}
              </div>

              {order.customer?.street && (
                <div className="border-t border-gray-200 pt-3 text-sm text-gray-600 dark:border-gray-700 dark:text-gray-400">
                  <p>{order.customer.street}</p>
                  <p>
                    {order.customer.zip} {order.customer.city}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Akce
            </h2>

            <StatusActions
              orderId={order.id}
              currentStatus={order.status}
              totalPrice={order.totalPrice || 0}
              depositAmount={order.depositAmount || 0}
              onStatusChange={() => refetch()}
            />
          </div>

          {/* Key dates */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Důležité termíny
            </h2>

            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Vytvořeno</span>
                <span className="text-gray-900 dark:text-white">
                  {formatDate(order.createdAt)}
                </span>
              </div>
              {order.depositDueDate && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">
                    Splatnost zálohy
                  </span>
                  <span className="text-gray-900 dark:text-white">
                    {formatDate(order.depositDueDate)}
                  </span>
                </div>
              )}
              {order.fullPaymentDue && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">
                    Splatnost doplatku
                  </span>
                  <span className="text-gray-900 dark:text-white">
                    {formatDate(order.fullPaymentDue)}
                  </span>
                </div>
              )}
              {order.tripDate && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">
                    Odjezd
                  </span>
                  <span className="font-medium text-primary-600">
                    {formatDate(order.tripDate.startDate)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
